import { Box } from '@material-ui/core';
import { lighten, makeStyles, Theme } from '@material-ui/core/styles';
import { LoginLogoImg } from '@roc/feature-app-core';
import {
  formatDate as _formatDate,
  formatBorrowerExperience,
} from '@roc/feature-utils';
import { PrintableArea } from '@roc/ui';
import {
  formatCurrency as _formatCurrency,
  formatPercentage as _formatPercentage,
} from '@roc/ui/utils';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { forwardRef } from 'react';
import { addressToString } from '../../utils/quoteUtils';
import { mapDownloadableQuoteDataTerm } from '../../utils/summaryUtils';
import { getExperienceOptions } from '../../utils/utils';

const formatCurrency = value => {
  return value ? _formatCurrency(value) : 'N/A';
};

const formatPercentage = value => {
  return value ? _formatPercentage(value, 4) : 'N/A';
};

const formatDate = value => {
  return value ? _formatDate(value, 'MMMM d, yyyy') : 'N/A';
};

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    color: theme.palette.text.primary,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'verdana, arial, sans-serif',
    lineHeight: '1.2',
    fontSize: '9.5pt',
    position: 'relative',

    ['& p']: {
      margin: '1em 0',
    },
    ['& li']: {
      margin: '0.5em 0',
      marginLeft: '2em',
    },
    ['& sup']: {
      fontSize: '80%',
      verticalAlign: 'super',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  table: {
    borderCollapse: 'collapse',
    ['& td']: {
      borderCollapse: 'collapse',
      padding: '5pt',
      border: '0.25pt solid ' + lighten(theme.palette.primary.dark, 0.5),
    },
    ['& th']: {
      color: 'black',
      textAlign: 'center',
      padding: '12px 0px',
    },
    ['& td:first-child']: {
      borderLeft: 0,
    },
    ['& td:last-child']: {
      borderRight: 0,
    },
  },
  thickRow: {
    borderTop: '2pt solid ' + lighten(theme.palette.primary.dark, 0.3),
  },
  header: {
    paddingBottom: '0.2in',
  },
  logoContainer: {
    margin: 0,
  },
  headerLine: {
    borderBottom: '4px solid ' + theme.palette.primary.main,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    justfyContent: 'flex-end',
    paddingTop: '0.2in',
  },
  title: {
    fontSize: '18pt',
    fontWeight: 'bold',
    paddingBottom: '2px',
  },
  subtitle: {
    fontSize: '18pt',
    paddingBottom: '12px',
  },
  bold: {
    fontWeight: 'bold',
  },
  center: {
    textAlign: 'center',
  },
  darkBackground: {
    backgroundColor: theme.palette.primary.light,
    fontWeight: 'bold',
    textAlign: 'right',
    color: 'black',
  },
  note: {
    fontSize: '6pt',
    color: 'rgb(102 102 102)',
  },
  section: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    padding: '4pt 0',
  },
  showOnPrint: {
    display: 'none',
  },
  quotesTable: {
    ['& tr td']: {
      textAlign: 'center',
    },
    ['& tr td:first-child']: {
      fontWeight: 'bold',
      textAlign: 'left',
    },
  },
  informationSharedWithUsTable: {
    ['& td']: {
      width: '25%',
    },
  },
  warnings: {
    color: theme.palette.error.main,
  },
  print: {
    //A4 size page
    width: '8.3in',
    height: '11.7in',

    //Add paddings
    padding: '0 0.65in',
    ['& $header']: {
      paddingTop: '0.6in',
    },

    ['& $footer']: {
      paddingBottom: '0.6in',
    },

    //Display hidden elements
    ['& $showOnPrint']: {
      display: 'block',
    },
  },
  freeAppraisalText: {
    marginTop: '10px',
  },
}));

interface DownloadableQuoteHtmlProps {
  store: any;
}

export const DownloadableQuoteTerm = observer(
  forwardRef<any, DownloadableQuoteHtmlProps>(({ store }, ref) => {
    const classes = useStyles();
    const lenderName = store.globalStore?.lenderInfo?.lenderTradeName;
    const data = mapDownloadableQuoteDataTerm(store);

    return (
      <PrintableArea printClass={classes.print} ref={ref}>
        <div className={classes.page}>
          <Header />
          <Content>
            <div className={classes.headerLine}>
              <div className={classes.title}>Conditional Quote</div>
              <div className={classes.subtitle}>Rental Loan Program</div>
            </div>
            <p>
              <b>{`Re: ${data.properties.propertyAddress}`}</b>
            </p>
            <p>
              Thank you for considering {lenderName}! After reviewing the
              information provided, below is a summary of our proposed terms.
              Contact us with any questions, and we look forward to working with
              you!
            </p>
            <InformationSharedWithUs data={data} store={store} />
            <br />
            <TheConditionalTermsWeCanOffer store={store} data={data} />
          </Content>
          <Footer className={classes.showOnPrint} />
        </div>
      </PrintableArea>
    );
  })
);

interface InformationSharedWithUsProps {
  data: any;
  store: any;
}

const InformationSharedWithUs = ({
  data,
  store,
}: InformationSharedWithUsProps) => {
  const classes = useStyles();
  const { loanSubtype } = store;
  const propertyData = data.properties;

  return (
    <>
      <div className={classes.section}>Information Shared With Us:</div>
      <table
        className={clsx(classes.table, classes.informationSharedWithUsTable)}
      >
        <tr>
          <td className={classes.bold}>Property Address:</td>
          <td colSpan={3}>{propertyData.propertyAddress}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Estimated As-Is Value*:</td>
          <td>{formatCurrency(propertyData.totalEstimatedAsIsValue)}</td>
          <td className={classes.bold}>Property County:</td>
          <td>{propertyData.propertyCounty}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Property Type:</td>
          <td>{propertyData.propertyType}</td>
          <td className={classes.bold}>Acquisition Date:</td>
          <td>{formatDate(propertyData.acquisitionDate)}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Total Acquisition Cost:</td>
          <td>{formatCurrency(propertyData.totalAcquisitionCost)}</td>
          <td className={classes.bold}>Completed Renovations:</td>
          <td>{formatCurrency(propertyData.completedRenovations)}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Monthly Expenses:</td>
          <td>{formatCurrency(propertyData.monthlyExpenses)}</td>
          <td className={classes.bold}>Monthly Rent*:</td>
          <td>{formatCurrency(propertyData.monthlyRent)}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Borrower’s Experience:</td>
          <td>{getExperienceOptions(loanSubtype).find(option => option.value === data.borrowersExperience)?.label}</td>
          <td className={classes.bold}>Estimated Credit Score:</td>
          <td>{data.estimatedCreditScore}</td>
        </tr>
        <tr>
          <td className={classes.bold}>Citizenship Status:</td>
          <td>{data.citizenshipStatus}</td>
          <td />
          <td />
        </tr>
      </table>
    </>
  );
};

const TheConditionalTermsWeCanOffer = ({ data, store }) => {
  const classes = useStyles();
  const pricingOptions = data.pricingOptions;

  return (
    <>
      <div className={classes.section}>Pricing:</div>
      <table className={clsx(classes.table, classes.quotesTable)}>
        <tr>
          <td>LTV</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{formatPercentage(option.loanToValue)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Loan Amount</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{formatCurrency(option.loanAmount)}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Loan Purpose</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{option.loanPurpose}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Amortization</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{option.amortizationType}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Pre-Payment Penalty</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{option.prePaymentPenalty}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Rate Type</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{option.rateType}</Box>
            </td>
          ))}
        </tr>
        <tr>
          <td>Today's Rate</td>
          {pricingOptions.map((option, i) => (
            <td>{formatPercentage(option.interestRate)}</td>
          ))}
        </tr>
        <tr>
          <td>Monthly Mortgage Payment (Principal and Interest Payments)</td>
          {pricingOptions.map((option, i) => (
            <td>
              <Box>{formatCurrency(option.estimatedMonthlyPayment)}</Box>
            </td>
          ))}
        </tr>
        {pricingOptions.some(opt => opt.brokerFee > 0) ? (
          <tr>
            <td>Broker Fee</td>
            {pricingOptions.map((option, i) => (
              <td>
                <Box>{formatPercentage(option.brokerFee)}</Box>
              </td>
            ))}
          </tr>
        ) : null}
      </table>
    </>
  );
};

const Header = ({ className = '', isQuote = false }) => {
  const classes = useStyles();
  return (
    <div className={`${classes.header} ${className}`}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <LoginLogoImg
          containerClassName={classes.logoContainer}
          imgStyle={{ height: '0.8in' }}
        />
      </div>
    </div>
  );
};

const Footer = ({ className = '' }) => {
  const classes = useStyles();
  return (
    <div className={`${classes.footer} ${className}`}>
      <p className={classes.note}>
        Please note this letter of interest serves to outline the terms of the
        proposed financing of the referenced transaction.This letter is merely a
        general proposal, and is neither a binding offer, nor a contract.
        Borrower understands that no such offer will be forthcoming prior to
        completion of appropriate due diligence and underwriting performed
        and/or contracted by Lender. This proposal does not create any legally
        binding obligations on any party hereto. All properties are subject to
        satisfactory Lender due diligence underwriting including: satisfactory
        appraisal, satisfactory credit review of the borrowing entity and Key
        Principals, and satisfactory review of the property’s market and
        submarket.
      </p>
    </div>
  );
};
const Content = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.content}>{children}</div>;
};
